import React from 'react';
import { Heart, ExternalLink, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { mockData } from '../mock';

const Donate = () => {

  return (
    <div className="min-h-screen">
      {/* Hero Section with Donate Button */}
      <section className="bg-gradient-to-br from-red-50 to-white pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Support Our Mission
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Your generosity brings healthcare to those who need it most. 100% of your donation goes directly to our medical missions.
          </p>
          
          {/* Donate Button */}
          <a
            href={mockData.donation.gofundme}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <Button
              className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-9 w-full max-w-md bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 transition-all transform hover:scale-105 hover:shadow-2xl sm:!text-2xl !font-medium !text-xl gap-2 px-4 mx-auto py-8 rounded-lg shadow-xl text-white"
            >
              <ExternalLink className="mr-3 h-7 w-7" />
              Donate via GoFundMe
            </Button>
          </a>
          
          <p className="text-sm text-gray-500 mt-6" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            You'll be redirected to our secure GoFundMe page
          </p>
        </div>
      </section>

      {/* Transparency Statement */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start space-x-4">
            <Shield className="h-8 w-8 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Our Commitment to Transparency
              </h3>
              <p className="text-blue-100 text-lg" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                {mockData.donation.transparencyStatement}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why GoFundMe Section */}
      <section id="donation-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <Card className="border-none shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Why GoFundMe?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <h5 className="font-semibold text-gray-900 mb-2 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Secure
                  </h5>
                  <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Industry-leading security and fraud protection
                  </p>
                </div>
                <div className="text-center">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h5 className="font-semibold text-gray-900 mb-2 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Trusted
                  </h5>
                  <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Used by millions worldwide for charitable giving
                  </p>
                </div>
                <div className="text-center">
                  <Heart className="h-12 w-12 text-red-500 fill-red-500 mx-auto mb-4" />
                  <h5 className="font-semibold text-gray-900 mb-2 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Transparent
                  </h5>
                  <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Track our progress and see your impact
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Other Ways to Support
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full mb-6">
                  <Heart className="h-8 w-8 text-white fill-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Corporate Matching
                </h3>
                <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Many employers will match your donation. Check if your company has a matching gift program.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-600 rounded-full mb-6">
                  <Heart className="h-8 w-8 text-white fill-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  In-Kind Donations
                </h3>
                <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  We accept donations of medical supplies, equipment, and other essential items.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-full mb-6">
                  <Heart className="h-8 w-8 text-white fill-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Legacy Giving
                </h3>
                <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Include Medics on Mission in your estate planning to create lasting impact.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>);

};

export default Donate;